import { useEffect } from 'react';
import { cleanInputString } from '../utils/stringFormatter';

export const useGlobalTextFormatter = () => {
  useEffect(() => {
    const handleFocusOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      // We only care about inputs and textareas
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement
      ) {
        const inputName = target.name?.toLowerCase() || '';
        const inputId = target.id?.toLowerCase() || '';
        const placeholder = target.placeholder?.toLowerCase() || '';
        const role = target.getAttribute('role')?.toLowerCase() || '';
        const autoCap =
          target.getAttribute('autocapitalize')?.toLowerCase() || '';

        // Explicit opt-outs for search inputs, query fields, or fields that shouldn't be auto-formatted/capitalized
        if (
          target.hasAttribute('data-no-format') ||
          target.hasAttribute('data-no-capitalize') ||
          role === 'searchbox' ||
          role === 'search' ||
          role === 'combobox' ||
          inputName.includes('search') ||
          inputName.includes('query') ||
          inputName.includes('filter') ||
          inputId.includes('search') ||
          inputId.includes('query') ||
          inputId.includes('filter') ||
          placeholder.includes('search') ||
          target.getAttribute('aria-label')?.toLowerCase().includes('search') ||
          autoCap === 'none' ||
          autoCap === 'off' ||
          Boolean(target.closest('[role="search"]')) ||
          Boolean(target.closest('[role="combobox"]')) ||
          Boolean(target.closest('.react-select__control'))
        ) {
          return;
        }

        // Skip types where formatting might be destructive
        if (
          target instanceof HTMLInputElement &&
          ([
            'email',
            'password',
            'url',
            'number',
            'tel',
            'search',
            'date',
            'time',
            'datetime-local',
            'file',
            'color',
            'range',
          ].includes(target.type) ||
            inputName.includes('email') ||
            inputName.includes('password') ||
            inputName.includes('credential') ||
            inputId.includes('email') ||
            inputId.includes('password') ||
            inputId.includes('credential'))
        ) {
          return;
        }

        const originalValue = target.value;
        const cleanedValue = cleanInputString(originalValue);

        if (cleanedValue !== originalValue) {
          // React intercepts standard value setters, so we need to bypass it
          // to correctly trigger the 'input' event that React relies on.
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value',
          )?.set;

          const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value',
          )?.set;

          if (target instanceof HTMLInputElement && nativeInputValueSetter) {
            nativeInputValueSetter.call(target, cleanedValue);
          } else if (
            target instanceof HTMLTextAreaElement &&
            nativeTextAreaValueSetter
          ) {
            nativeTextAreaValueSetter.call(target, cleanedValue);
          } else {
            target.value = cleanedValue;
          }

          // Dispatch input event for React (and React Hook Form) to catch
          target.dispatchEvent(new Event('input', { bubbles: true }));
          // Also dispatch change event just in case
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };

    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
};
